import React from 'react';
import { useTranslation } from '../../i18n';
import './AnswerFeedbackCard.css';

interface AnswerFeedbackCardProps {
  isCorrect: boolean;
  showDidYouKnow: boolean;
  interestingFact?: string;
  memeUrl?: string | null;
  correctAnswerLabel?: string;
  wrongAnswerLabel?: string;
  correctAnswer?: string;
  acceptedAnswers?: string[];
  rankText?: string;
  pointsEarned?: number;
}

const AnswerFeedbackCard: React.FC<AnswerFeedbackCardProps> = ({
  isCorrect,
  showDidYouKnow,
  interestingFact,
  memeUrl,
  correctAnswerLabel,
  wrongAnswerLabel,
  correctAnswer,
  acceptedAnswers,
  rankText,
  pointsEarned,
}) => {
  const { t } = useTranslation();
  const resolvedCorrectLabel = correctAnswerLabel ?? t('gameplay.feedback.correctAnswer');
  const resolvedWrongLabel = wrongAnswerLabel ?? t('gameplay.feedback.wrongAnswer');

  const hasFlip = showDidYouKnow && (interestingFact || memeUrl);
  const hasBack = !!(interestingFact || memeUrl);
  const useGrid = hasBack;
  const toneClass = isCorrect ? 'answer-feedback-card__face--correct' : 'answer-feedback-card__face--wrong';

  return (
    <div className="answer-feedback-card">
      <div
        className={`answer-feedback-card__flip${hasFlip ? ' answer-feedback-card__flip--flipped' : ''}${!useGrid ? ' answer-feedback-card__flip--no-grid' : ''}`}
      >
        <div
          className={`answer-feedback-card__face answer-feedback-card__face--front ${toneClass} ${
            !hasFlip ? 'answer-feedback-card__face--visible' : ''
          }`}
        >
          <div className="answer-feedback-card__result-row">
            <span className="answer-feedback-card__result-icon">
              {isCorrect ? '✓' : '✗'}
            </span>
            <span className="answer-feedback-card__result-label">
              {isCorrect
                ? (rankText ? `${rankText} (+${pointsEarned})` : resolvedCorrectLabel)
                : resolvedWrongLabel}
            </span>
          </div>

          {!isCorrect && correctAnswer && (
            <div className="answer-feedback-card__wrong-details">
              <p className="answer-feedback-card__correct-answer-label">
                {t('gameplay.feedback.correctAnswerColon')}
              </p>
              <p className="answer-feedback-card__correct-answer">
                {correctAnswer}
              </p>
              {acceptedAnswers && acceptedAnswers.length > 1 && (
                <p className="answer-feedback-card__accepted">
                  {t('gameplay.feedback.alsoAccepted', {
                    answers: acceptedAnswers.filter(a => a !== correctAnswer).join(', '),
                  })}
                </p>
              )}
            </div>
          )}
        </div>

        {(interestingFact || memeUrl) && (
          <div
            className={`answer-feedback-card__face answer-feedback-card__face--back ${toneClass} ${
              hasFlip ? 'answer-feedback-card__face--visible' : ''
            }`}
          >
            {memeUrl && (
              <div style={{ marginBottom: '1rem', borderRadius: '12px', overflow: 'hidden', display: 'flex', justifyContent: 'center' }}>
                <img src={memeUrl} alt="" style={{ maxWidth: '100%', maxHeight: '240px', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block' }} />
              </div>
            )}
            {interestingFact && (
              <>
                <p className="wcib-did-you-know-title" style={{ fontWeight: 'bold', margin: '0 0 0.8rem 0' }}>
                  {t('gameplay.feedback.didYouKnow')}
                </p>
                <p className="wcib-did-you-know-body" style={{ lineHeight: '1.6', margin: 0 }}>
                  {interestingFact}
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnswerFeedbackCard;
